// Community Stream Tab: Zeigt live Partner-Streamer + Kisten-System (NT-CONSUMABLE-01)
// Als Dashboard-Tab integriert – kein eigenes Layout.

import { api, ApiError } from '../../services/api.js';
import { navigate } from '../../router.js';
import { showToast } from '../../utils/toast.js';
import { formatNox } from '../../utils/formatters.js';
import { refreshBalance } from '../../utils/refreshBalance.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LiveStreamer {
  id: string;
  displayName: string;
  twitchUsername: string;
  slug: string;
  isLive: boolean;
  lastLiveAt: string | null;
}

interface StreamCodeRedeemResult {
  rewardType: string;
  rewardAmountCents: string;
}

interface ChestAvailableEntry {
  chestType:  string;
  costPoints: number;
  dropRates:  Record<string, number>;
}
interface ChestsAvailableResponse { points: number; chests: ChestAvailableEntry[]; }
interface ChestOpenResult {
  rarity: string;
  itemType: string;
  nameDE: string;
  effectDescriptionDE: string;
  pointsRemaining: number;
}

// ─── Anzeige-Labels (kein Shared-Import für reine Anzeige-Werte, etabliertes Pattern) ──

const CHEST_LABELS: Record<string, string> = {
  STRASSEN_KISTE: 'STRASSENKISTE',
  HEHLER_KISTE: 'HEHLER-KISTE',
  CRIME_BOSS_KISTE: 'CRIME-BOSS-KISTE',
  MYSTERIUM_KISTE: 'MYSTERIUM-KISTE',
};

// Rarity-Farben (dupliziert aus lagerhaus.ts::CONSUMABLE_RARITY_VAR, kein Shared-Import für
// reine Anzeige-Werte, etabliertes Pattern) — genre-übliche Folge grau → grün → blau → lila,
// muss mit dieser Zuordnung synchron bleiben.
const RARITY_COLOR_VAR: Record<string, string> = {
  COMMON: '--text-muted',
  UNCOMMON: '--accent-green',
  RARE: '--accent-blue',
  EPIC: '--accent-purple',
};
function rarityColorVar(rarity: string): string {
  return RARITY_COLOR_VAR[rarity] ?? '--text-muted';
}

// ─── State ────────────────────────────────────────────────────────────────────

let streamPollingInterval: ReturnType<typeof setInterval> | null = null;
let streamMounted = false;

let chestsData: ChestsAvailableResponse | null = null;
const expandedChestOdds = new Set<string>();
let chestOpeningInProgress = false;

// ─── Kisten-Öffnungs-Animation ─────────────────────────────────────────────────
// Zwei-Phasen-Zustand statt sofortigem Ergebnis-Popup: 'opening' zeigt die reine
// Kisten-Grafik (Schütteln → Deckel auf → Funken/Schockwelle in Rarity-Farbe), 'revealed'
// blendet den bekannten Ergebnistext ein.
//
// Der komplette Vor-Burst-Ablauf (Wackeln → Schloss löst sich → Deckel klappt mit leichtem
// Überschwung auf) ist bei jeder Rarity IDENTISCH — nur die anschließende "Burst"-Phase
// (Funkenzahl, Schockwellen-Ringe, Screen-Flash) skaliert mit der Seltenheit, damit ein
// EPIC-Pull spürbar mehr Spektakel macht als ein COMMON-Pull. Alle Zeitkonstanten liegen
// hier als einzige Quelle der Wahrheit — die @keyframes in community.css definieren nur die
// FORM der Animation (Kurve/Dauer), die tatsächlichen Verzögerungen pro Element (Funke N,
// Ring N) werden hier berechnet und als Inline-`animation-delay` mitgerendert.
type ChestOpenPhase = 'opening' | 'revealed';
interface ChestOpenAnimState { result: ChestOpenResult; phase: ChestOpenPhase; }

const CHEST_ANIM_LID_END_S    = 1.06; // Deckel ist vollständig auf (inkl. Überschwung-Settle)
const CHEST_ANIM_BURST_START_S = 0.92; // Funken/Ringe setzen leicht überlappend mit dem Deckel ein
const CHEST_ANIM_SPARK_STAGGER_S  = 0.032;
const CHEST_ANIM_SPARK_DURATION_S = 0.45;
const CHEST_ANIM_RING_STAGGER_S   = 0.16;
const CHEST_ANIM_RING_DURATION_S  = 0.55;
const CHEST_ANIM_FLASH_START_S    = 0.80;
const CHEST_ANIM_REVEAL_BUFFER_S  = 0.15;

interface ChestRarityTier { sparkCount: number; ringCount: number; flash: boolean; }
// Skaliert mit Seltenheit: mehr Funken, mehr Ringe, ab RARE zusätzlich ein kurzer Screen-Flash.
const CHEST_RARITY_TIER: Record<string, ChestRarityTier> = {
  COMMON:   { sparkCount: 6,  ringCount: 0, flash: false },
  UNCOMMON: { sparkCount: 9,  ringCount: 1, flash: false },
  RARE:     { sparkCount: 11, ringCount: 1, flash: true },
  EPIC:     { sparkCount: 15, ringCount: 2, flash: true },
};
function chestRarityTier(rarity: string): ChestRarityTier {
  return CHEST_RARITY_TIER[rarity] ?? { sparkCount: 6, ringCount: 0, flash: false };
}

/** Zeitpunkt, ab dem der Ergebnistext eingeblendet wird — knapp nach dem letzten Funken/Ring. */
function chestRevealDelayMs(rarity: string): number {
  const tier = chestRarityTier(rarity);
  const lastSparkEnd = CHEST_ANIM_BURST_START_S
    + (tier.sparkCount - 1) * CHEST_ANIM_SPARK_STAGGER_S + CHEST_ANIM_SPARK_DURATION_S;
  const lastRingEnd = tier.ringCount > 0
    ? CHEST_ANIM_BURST_START_S + (tier.ringCount - 1) * CHEST_ANIM_RING_STAGGER_S + CHEST_ANIM_RING_DURATION_S
    : 0;
  const totalS = Math.max(lastSparkEnd, lastRingEnd, CHEST_ANIM_LID_END_S) + CHEST_ANIM_REVEAL_BUFFER_S;
  return Math.round(totalS * 1000);
}

let chestOpenAnim: ChestOpenAnimState | null = null;
let chestOpenRevealTimer: ReturnType<typeof setTimeout> | null = null;

// ─── Helfer ───────────────────────────────────────────────────────────────────

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

// ─── HTML-Shell ───────────────────────────────────────────────────────────────

export function renderStreamTab(): string {
  return `
    <section class="dash-content" id="dash-stream">
      <div class="stream-header">
        <h2>COMMUNITY – STREAM</h2>
      </div>
      <div class="stream-code-section">
        <div class="stream-code-label">CODE AUS DEM STREAM?</div>
        <form id="stream-code-form" class="stream-code-form">
          <input
            type="text"
            id="stream-code-input"
            class="stream-code-input"
            placeholder="z.B. PFANDBON-7F3Q"
            maxlength="24"
            autocomplete="off"
          />
          <button type="submit" class="btn-stream-code-redeem">EINLÖSEN</button>
        </form>
      </div>
      <div id="stream-chest-section">
        <div class="stream-loading">LADE PUNKTESTAND...</div>
      </div>
      <div id="stream-live-section">
        <div class="stream-loading">LADE STREAM-STATUS...</div>
      </div>
    </section>
  `;
}

// ─── Mount ────────────────────────────────────────────────────────────────────

export function mountStreamTab(): void {
  if (streamMounted) return;
  streamMounted = true;

  bindStreamCodeForm();
  void loadStreamers();
  void loadChests();

  streamPollingInterval = setInterval(() => {
    void loadStreamers();
    // Kein Refresh während eine Kisten-Animation läuft oder das Ergebnis-Popup offen ist —
    // ein voller innerHTML-Rebuild würde die laufenden CSS-Animationen neu starten (Flacker-Risiko).
    if (chestOpenAnim === null) void loadChests();
  }, 60_000);
}

export function cleanupStreamTab(): void {
  if (streamPollingInterval !== null) {
    clearInterval(streamPollingInterval);
    streamPollingInterval = null;
  }
  if (chestOpenRevealTimer !== null) {
    clearTimeout(chestOpenRevealTimer);
    chestOpenRevealTimer = null;
  }
  streamMounted = false;
  chestsData = null;
  chestOpenAnim = null;
  chestOpeningInProgress = false;
  expandedChestOdds.clear();
}

// ─── Codewort-Drop einlösen (NT-STREAM-CODE-01) ────────────────────────────────

function bindStreamCodeForm(): void {
  const form = document.getElementById('stream-code-form');
  const input = document.getElementById('stream-code-input');
  if (!(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void handleRedeemStreamCode(input);
  });
}

async function handleRedeemStreamCode(input: HTMLInputElement): Promise<void> {
  const code = input.value.trim();
  if (!code) return;

  const submitBtn = document.querySelector<HTMLButtonElement>('.btn-stream-code-redeem');
  if (submitBtn) submitBtn.disabled = true;
  input.disabled = true;

  try {
    const result = await api.post<StreamCodeRedeemResult>('/stream-codes/redeem', { code });
    showToast(`Code eingelöst! +${formatNox(result.rewardAmountCents)} gutgeschrieben.`);
    input.value = '';
    void refreshBalance();
  } catch (err) {
    // Einheitliche Fehlermeldung vom Backend (S3.3) — 1:1 übernommen, kein eigener Text.
    const msg = err instanceof ApiError ? err.message : 'Code konnte nicht eingelöst werden.';
    showToast(msg, 'error');
  } finally {
    if (submitBtn) submitBtn.disabled = false;
    input.disabled = false;
  }
}

// ─── Daten laden: Live-Streamer ────────────────────────────────────────────────

async function loadStreamers(): Promise<void> {
  const container = document.getElementById('stream-live-section');
  if (!container) return;

  try {
    const streamers = await api.get<LiveStreamer[]>('/community/live');
    renderStreamers(container, streamers);
  } catch {
    container.innerHTML = '<div class="stream-error">STREAM-STATUS NICHT VERFÜGBAR.</div>';
  }
}

function renderStreamers(container: HTMLElement, streamers: LiveStreamer[]): void {
  const live = streamers.filter((s) => s.isLive);

  if (live.length === 0) {
    container.innerHTML = `
      <div class="stream-offline-msg">
        <div class="stream-offline-label">NIEMAND IST GERADE LIVE.</div>
        <div class="stream-offline-sub">MR. WEEDMAN LIEFERT GERADE EIS.<br>SCHAU SPÄTER NOCHMAL VORBEI.</div>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="stream-live-label">JETZT LIVE</div>
    <div class="stream-cards">
      ${live.map(renderStreamerCard).join('')}
    </div>
  `;

  container.querySelectorAll<HTMLButtonElement>('[data-action="open-community"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const slug = btn.dataset.slug;
      if (slug) navigate(`/community/${slug}`);
    });
  });
}

function renderStreamerCard(streamer: LiveStreamer): string {
  const liveSince = streamer.lastLiveAt
    ? formatLiveDuration(new Date(streamer.lastLiveAt))
    : '';

  return `
    <div class="stream-card">
      <div class="stream-card-name">
        <span class="stream-live-dot"></span>
        ${streamer.displayName.toUpperCase()}
      </div>
      ${liveSince ? `<div class="stream-card-since">LIVE SEIT ${liveSince}</div>` : ''}
      <div class="stream-card-actions">
        <a
          href="https://twitch.tv/${streamer.twitchUsername}"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-stream-twitch"
        >AUF TWITCH SCHAUEN</a>
        <button class="btn-stream-community" data-action="open-community" data-slug="${streamer.slug}">
          ZUR COMMUNITY
        </button>
      </div>
    </div>
  `;
}

function formatLiveDuration(since: Date): string {
  const ms = Date.now() - since.getTime();
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h > 0) return `${String(h)}H ${String(m)}MIN`;
  return `${String(m)}MIN`;
}

// ─── Daten laden: Kisten (NT-CONSUMABLE-01, Session 2/N) ──────────────────────

async function loadChests(): Promise<void> {
  const container = document.getElementById('stream-chest-section');
  if (!container) return;

  try {
    chestsData = await api.get<ChestsAvailableResponse>('/chests/available');
    renderChestSection(container);
  } catch {
    container.innerHTML = '<div class="stream-error">KISTEN-STATUS NICHT VERFÜGBAR.</div>';
  }
}

function renderChestOddsRows(dropRates: Record<string, number>): string {
  return Object.entries(dropRates)
    .filter(([, weight]) => weight > 0)
    .map(([rarity, weight]) => `
      <div class="chest-odds-row">
        <span class="chest-odds-rarity" style="color:var(${rarityColorVar(rarity)});">${esc(rarity)}</span>
        <span class="chest-odds-pct">${String(weight)}%</span>
      </div>`)
    .join('');
}

function renderChestCard(chest: ChestAvailableEntry, points: number): string {
  const label = CHEST_LABELS[chest.chestType] ?? chest.chestType;
  const canAfford = points >= chest.costPoints;
  const oddsOpen = expandedChestOdds.has(chest.chestType);

  return `
    <div class="chest-card">
      <div class="chest-card-head">
        <span class="chest-card-name">${esc(label)}</span>
        <span class="chest-card-cost">${String(chest.costPoints)} PKT.</span>
      </div>
      <button class="chest-odds-toggle" data-chest-odds="${esc(chest.chestType)}">
        ${oddsOpen ? 'QUOTEN VERBERGEN' : 'QUOTEN ANZEIGEN'}
      </button>
      ${oddsOpen ? `<div class="chest-odds">${renderChestOddsRows(chest.dropRates)}</div>` : ''}
      <button class="btn-stream-chest-open" data-chest-open="${esc(chest.chestType)}" ${!canAfford ? 'disabled' : ''}>
        ÖFFNEN
      </button>
    </div>`;
}

/** N Funkenlinien, gleichmäßig radial verteilt, mit wechselnder Länge für eine unregelmäßigere,
 *  "echtere" Burst-Silhouette. Verzögerung wird direkt inline gesetzt (Single Source of Truth
 *  ist die JS-Zeitachse oben, keine hartcodierten :nth-child-Regeln in CSS). */
function chestSparkLines(count: number): string {
  const cx = 60;
  const cy = 45;
  const lines: string[] = [];
  for (let i = 0; i < count; i++) {
    const angle = ((360 / count) * i - 90) * (Math.PI / 180);
    const len = i % 2 === 0 ? 21 : 14;
    const x2 = (cx + Math.cos(angle) * len).toFixed(1);
    const y2 = (cy + Math.sin(angle) * len).toFixed(1);
    const delayS = (CHEST_ANIM_BURST_START_S + i * CHEST_ANIM_SPARK_STAGGER_S).toFixed(3);
    lines.push(
      `<line x1="${String(cx)}" y1="${String(cy)}" x2="${x2}" y2="${y2}" style="animation-delay:${delayS}s"></line>`,
    );
  }
  return lines.join('');
}

/** N konzentrische Schockwellen-Ringe, zeitlich versetzt — ab RARE zusätzlich zu den Funken. */
function chestShockRings(count: number): string {
  const rings: string[] = [];
  for (let i = 0; i < count; i++) {
    const delayS = (CHEST_ANIM_BURST_START_S + i * CHEST_ANIM_RING_STAGGER_S).toFixed(3);
    rings.push(`<circle class="chest-ring" cx="60" cy="45" r="9" style="animation-delay:${delayS}s"></circle>`);
  }
  return rings.join('');
}

// Reine Linienzeichnung im Terminal-Stil (kantig, keine Rundungen/Verläufe/Schatten) — kein
// Bild-Asset, Farbgebung ausschließlich über CSS Custom Properties. Kiste + Schloss bleiben
// IMMER neutral (`--border`/`--accent-gold`, unabhängig von der Rarity) — erst der Burst
// (Funken/Ring/Flash) verrät per `currentColor`, was gezogen wurde. Vier Eck-Nieten + ein
// echtes Vorhängeschloss (Bügel + Kasten) statt eines simplen Rechtecks für mehr Charakter.
function renderChestGraphic(rarity: string, isOpening: boolean): string {
  const tier = chestRarityTier(rarity);
  const flashDelay = CHEST_ANIM_FLASH_START_S.toFixed(3);
  return `
    <div class="chest-open-anim ${isOpening ? 'is-opening' : 'is-done'}" style="color:var(${rarityColorVar(rarity)});">
      <svg viewBox="0 0 120 100" aria-hidden="true" focusable="false">
        <g class="chest-rings">${chestShockRings(isOpening ? tier.ringCount : 0)}</g>
        <g class="chest-sparks">${chestSparkLines(isOpening ? tier.sparkCount : 0)}</g>
        <g class="chest-pop-wrap">
          <g class="chest-shake-wrap">
            <rect class="chest-box-body" x="15" y="48" width="90" height="40"></rect>
            <line class="chest-box-detail" x1="42" y1="48" x2="42" y2="88"></line>
            <line class="chest-box-detail" x1="78" y1="48" x2="78" y2="88"></line>
            <line class="chest-box-detail" x1="15" y1="68" x2="105" y2="68"></line>
            <circle class="chest-rivet" cx="20" cy="53" r="1.6"></circle>
            <circle class="chest-rivet" cx="100" cy="53" r="1.6"></circle>
            <circle class="chest-rivet" cx="20" cy="83" r="1.6"></circle>
            <circle class="chest-rivet" cx="100" cy="83" r="1.6"></circle>
            <g class="chest-box-lid">
              <rect x="13" y="32" width="94" height="18"></rect>
              <circle class="chest-rivet" cx="19" cy="41" r="1.4"></circle>
              <circle class="chest-rivet" cx="101" cy="41" r="1.4"></circle>
            </g>
            <g class="chest-latch">
              <path d="M 57 42 A 3 3 0 0 1 63 42" fill="none"></path>
              <rect x="56" y="41" width="8" height="7"></rect>
              <circle class="chest-latch-hole" cx="60" cy="44.5" r="0.8"></circle>
            </g>
          </g>
        </g>
      </svg>
      ${isOpening && tier.flash ? `<div class="chest-flash" style="background-color:var(${rarityColorVar(rarity)});animation-delay:${flashDelay}s"></div>` : ''}
    </div>`;
}

function renderChestResultPopup(): string {
  if (!chestOpenAnim) return '';
  const { result: r, phase } = chestOpenAnim;
  const isOpening = phase === 'opening';
  return `
    <div class="modal-backdrop" id="stream-chest-result-modal">
      <div class="modal modal--narrow">
        <div class="modal-header">
          <h2>${isOpening ? 'KISTE ÖFFNET SICH...' : 'TREFFER!'}</h2>
        </div>
        <div class="modal-body">
          ${renderChestGraphic(r.rarity, isOpening)}
          <div class="chest-result-text ${isOpening ? 'chest-result-text--hidden' : 'chest-result-text--visible'}">
            <p class="chest-result-rarity" style="color:var(${rarityColorVar(r.rarity)});">${esc(r.rarity)}</p>
            <p class="chest-result-name">${esc(r.nameDE)}</p>
            <p class="chest-result-effect">${esc(r.effectDescriptionDE)}</p>
            <p class="chest-result-points">Punkte übrig: ${String(r.pointsRemaining)}</p>
          </div>
        </div>
        ${isOpening ? '' : `
        <div class="modal-actions">
          <button class="btn" data-close-chest-result>OK</button>
        </div>`}
      </div>
    </div>`;
}

function renderChestSection(container: HTMLElement): void {
  if (!chestsData) return;
  const points = chestsData.points;

  container.innerHTML = `
    <div class="chest-section-header">
      <span class="chest-section-title">DEINE PUNKTE</span>
      <span class="chest-points-value">${String(points)}</span>
    </div>
    <div class="chest-cards">
      ${chestsData.chests.map((c) => renderChestCard(c, points)).join('')}
    </div>
    ${renderChestResultPopup()}
  `;

  container.querySelectorAll<HTMLButtonElement>('[data-chest-odds]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const chestType = btn.dataset.chestOdds ?? '';
      if (expandedChestOdds.has(chestType)) expandedChestOdds.delete(chestType);
      else expandedChestOdds.add(chestType);
      renderChestSection(container);
    });
  });

  container.querySelectorAll<HTMLButtonElement>('[data-chest-open]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const chestType = btn.dataset.chestOpen ?? '';
      if (chestType) void handleOpenChest(chestType);
    });
  });

  const closeBtn = container.querySelector<HTMLButtonElement>('[data-close-chest-result]');
  closeBtn?.addEventListener('click', () => { closeChestResult(container); });
  const backdrop = document.getElementById('stream-chest-result-modal');
  backdrop?.addEventListener('click', (e) => {
    if (e.target === backdrop && chestOpenAnim?.phase === 'revealed') closeChestResult(container);
  });
}

/** Schließt nur, wenn die Animation bereits durchgelaufen ist — kein Wegklicken mittendrin. */
function closeChestResult(container: HTMLElement): void {
  if (chestOpenAnim?.phase !== 'revealed') return;
  chestOpenAnim = null;
  void loadChests(); // Punktestand/Kisten-Liste nach dem Schließen einmal frisch nachziehen.
  renderChestSection(container);
}

async function handleOpenChest(chestType: string): Promise<void> {
  if (chestOpeningInProgress) return;
  chestOpeningInProgress = true;

  const openBtn = document.querySelector<HTMLButtonElement>(`[data-chest-open="${chestType}"]`);
  if (openBtn) { openBtn.disabled = true; openBtn.textContent = '...'; }

  try {
    const result = await api.post<ChestOpenResult>(`/chests/${encodeURIComponent(chestType)}/open`, {});
    if (chestsData) chestsData.points = result.pointsRemaining;

    if (chestOpenRevealTimer !== null) clearTimeout(chestOpenRevealTimer);
    chestOpenAnim = { result, phase: 'opening' };
    chestOpenRevealTimer = setTimeout(() => {
      chestOpenRevealTimer = null;
      if (!chestOpenAnim) return;
      chestOpenAnim = { result: chestOpenAnim.result, phase: 'revealed' };
      const c = document.getElementById('stream-chest-section');
      if (c) renderChestSection(c);
    }, chestRevealDelayMs(result.rarity));
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Kiste konnte nicht geöffnet werden.';
    showToast(msg, 'error');
  } finally {
    chestOpeningInProgress = false;
    const container = document.getElementById('stream-chest-section');
    if (container) renderChestSection(container);
  }
}
